/**
 * Le bilan de séance du masteur (`end-session-tutor`) : ce que `<TutorCloseOutPage>` montre.
 *
 * Patron B : la clôture qualité et l'occurrence n'ont pas de réducteur ; leurs réponses arrivent de
 * `useTutorCloseOutData`. Ce que le masteur a dit vient des brouillons de l'écran
 * (`tutorCloseOutDrafts`), la prescription du champion des paramètres de la route, les étiquettes de
 * `static-data` (`tutorCloseOutTagCatalogs`).
 */
import { createSelector } from "reselect"

import {
    closeOutDateLabel,
    closeOutEngagementOf,
    closeOutStudentIdentity,
    noStudentAttendedOf,
    platformTagsPositive,
    productivityStateOf,
    qualityStudentsById,
    samActivitiesCountOf,
    studentAlertAxesOf,
    studentTagsPositive,
    studentTagsTitle,
    toCloseOutProxies,
    toTagChoices,
    type ApiCloseOutOccurrence,
    type ApiMissionPrescription,
    type ApiQualityClosure,
    type ApiTagCatalog,
    type TagChoice,
} from "../../../adapters/tutor/Lives/tutorCloseOutAdapter"
import {
    type UITutorCloseOutStudent,
    type UITutorComment,
    type UITutorPlatformFeedback,
    type UITutorQualityAxis,
} from "../../../ui/types/tutorCloseOut"
import {
    seedStudentDraft,
    type CloseOutCommentDraft,
    type CloseOutDrafts,
    type CloseOutPlatformDraft,
} from "./tutorCloseOutDrafts"

export type TutorCloseOutTagCatalogs = {
    studentPositive: TagChoice[]
    studentNegative: TagChoice[]
    platformPositive: TagChoice[]
    platformNegative: TagChoice[]
}

type StaticTags = {
    tutor_tags_positive?: ApiTagCatalog | null
    tutor_tags_negative?: ApiTagCatalog | null
    live_satisfaction_tags_positive?: ApiTagCatalog | null
    live_satisfaction_tags_negative?: ApiTagCatalog | null
}

const staticDataOf = (state: unknown) =>
    ((state as { data?: { staticData?: StaticTags | null } })?.data?.staticData ?? null) as StaticTags | null

/** Les étiquettes du bilan, mémoïsées sur `static-data`. */
export const tutorCloseOutTagCatalogs = createSelector([staticDataOf], (data): TutorCloseOutTagCatalogs => ({
    studentPositive: toTagChoices(data?.tutor_tags_positive),
    studentNegative: toTagChoices(data?.tutor_tags_negative),
    platformPositive: toTagChoices(data?.live_satisfaction_tags_positive),
    platformNegative: toTagChoices(data?.live_satisfaction_tags_negative),
}))

/** Les étiquettes d'un élève à cette note — les positives à partir de 4 étoiles. */
export const studentTagChoices = (catalogs: TutorCloseOutTagCatalogs, rating: number) =>
    studentTagsPositive(rating) ? catalogs.studentPositive : catalogs.studentNegative

/** Les étiquettes de la plateforme à cette note — les positives à 5 étoiles seulement. */
export const platformTagChoices = (catalogs: TutorCloseOutTagCatalogs, rating: number) =>
    platformTagsPositive(rating) ? catalogs.platformPositive : catalogs.platformNegative

/** La page rend le libellé d'une étiquette ; le serveur attend sa clé. */
export const tagKeyOf = (choices: TagChoice[], label: string) => choices.find((choice) => choice.label === label)?.key

const labelsOf = (choices: TagChoice[], keys: string[]) =>
    choices.filter((choice) => keys.includes(choice.key)).map((choice) => choice.label)

function toUIComment(draft: CloseOutCommentDraft): UITutorComment {
    return {
        mode: draft.mode,
        text: draft.text,
        voice: draft.voice,
        ...(draft.untranscribed ? { voiceUntranscribed: true } : {}),
        send: draft.send,
        ...(draft.sendError != null ? { sendError: draft.sendError } : {}),
    }
}

const EMPTY_PLATFORM: CloseOutPlatformDraft = {
    rating: 0,
    tags: [],
    mode: "text",
    text: "",
    voice: null,
    untranscribed: false,
    send: null,
}

export type TutorCloseOutArgs = {
    closure: ApiQualityClosure | null
    occurrence: ApiCloseOutOccurrence | null
    drafts: CloseOutDrafts
    /** Les paramètres de la route quand on arrive de la prescription : par élève. */
    prescriptions?: Record<string, ApiMissionPrescription | null | undefined> | null
    catalogs: TutorCloseOutTagCatalogs
}

export type TutorCloseOutViewModel = {
    dateLabel?: string
    proxies: UITutorQualityAxis[]
    noStudentAttended: { tutorConnectionMinutes: number } | null
    students: UITutorCloseOutStudent[]
    platform: UITutorPlatformFeedback
}

export function tutorCloseOutViewModel(args: TutorCloseOutArgs): TutorCloseOutViewModel {
    const { closure, occurrence, drafts, prescriptions, catalogs } = args
    const oralScoringEnabled = !!occurrence?.oral_scoring_enabled
    const qualityById = qualityStudentsById(closure)

    const students = (occurrence?.live_reports ?? []).map((report): UITutorCloseOutStudent => {
        const identity = closeOutStudentIdentity(report)
        if (report.statistics == null) return { ...identity, absent: true }
        const studentId = report.student?.id != null ? String(report.student.id) : null
        const quality = studentId != null ? qualityById.get(studentId) : undefined
        const productivity = productivityStateOf(report, oralScoringEnabled)
        const draft = drafts.students[String(report.id)] ?? seedStudentDraft(report)
        const choices = studentTagChoices(catalogs, draft.rating)
        const samActivitiesCount = samActivitiesCountOf(studentId != null ? prescriptions?.[studentId] : null)
        return {
            ...identity,
            absent: false,
            engagement: closeOutEngagementOf(report, quality, productivity),
            alertAxes: studentAlertAxesOf(quality, productivity),
            ...(samActivitiesCount != null ? { samActivitiesCount } : {}),
            rating: {
                rating: draft.rating,
                tagsTitle: studentTagsTitle(draft.rating),
                tags: choices.map((choice) => choice.label),
                selectedTags: labelsOf(choices, draft.tags),
            },
            comment: toUIComment(draft),
            done: draft.done,
        }
    })

    const platformDraft = drafts.platform ?? EMPTY_PLATFORM
    const platformChoices = platformTagChoices(catalogs, platformDraft.rating)

    return {
        dateLabel: closeOutDateLabel(occurrence?.date),
        proxies: toCloseOutProxies(closure),
        noStudentAttended: noStudentAttendedOf(closure),
        students,
        platform: {
            rating: {
                rating: platformDraft.rating,
                tags: platformChoices.map((choice) => choice.label),
                selectedTags: labelsOf(platformChoices, platformDraft.tags),
            },
            comment: toUIComment(platformDraft),
        },
    }
}
